import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Download, Images, FileText, Presentation, ClipboardList, KeyRound, ListChecks, CheckCircle2, GraduationCap } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const KIT_HERO = "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/173e1fe82aacd740c95ad1cf37ef091818d2f2b24a472ca45707eca3b54b11e5.jpeg";

const KIT_ITEMS = [
  { icon: Images, label: "Step Photos", desc: "All 15 lesson images + cover & finish" },
  { icon: FileText, label: "PDF Guide", desc: "Printable, step-by-step reference" },
  { icon: Presentation, label: "Keynote / PowerPoint", desc: "Ready-to-present slide deck" },
  { icon: ClipboardList, label: "Worksheet", desc: "Student activity sheet" },
  { icon: KeyRound, label: "Answer Key", desc: "For quick grading" },
  { icon: ListChecks, label: "Quiz", desc: "20-question multiple choice" },
];

export default function TeachingKit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("building"); // building | ready | error
  const [progress, setProgress] = useState(5);
  const [title, setTitle] = useState("");
  const [downloading, setDownloading] = useState(false);
  const started = useRef(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const { data } = await api.post(`/manuals/${id}/assets-only`);
        setStatus(data.status || "building");
        setProgress(data.progress || 5);
        poll();
      } catch (e) {
        if (e?.response?.status === 402) {
          toast.error("You need more balance to build this kit.");
          navigate("/pricing");
        } else {
          toast.error(e?.response?.data?.detail || "Couldn't start your kit — please try again.");
          setStatus("error");
        }
      }
    })();
    return () => clearInterval(pollRef.current);
  }, [id]); // eslint-disable-line

  const poll = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/manuals/${id}`);
        setTitle(data.title || "");
        const st = data.assets_status || "building";
        setProgress(Math.max(5, data.assets_progress || 5));
        if (st === "ready" || st === "error" || data.video_credited) {
          setStatus(st === "error" ? "error" : "ready");
          setProgress(100);
          clearInterval(pollRef.current);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  const downloadAll = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/manuals/${id}/teaching-kit.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}-teaching-kit.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your teaching kit is downloading 📦");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the zip — please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pb-20" data-testid="teaching-kit-page">
      <button onClick={() => navigate(`/manual/${id}`)} className="flex items-center gap-2 text-white font-bold hover:text-white/80 transition-colors py-4" data-testid="kit-back-btn">
        <ArrowLeft size={18} /> Back
      </button>

      {/* Hero graphic — placeholder art, swap later */}
      <div className="rounded-2xl overflow-hidden border-2 border-[#5d8a52] bg-black" data-testid="kit-hero">
        <img src={KIT_HERO} alt="Digital teaching kit" className="w-full h-auto object-cover" />
      </div>

      <div className="mt-5 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#0f0f16] bg-[#8fbf82] px-3 py-1 rounded-full">
          <GraduationCap size={14} /> Digital Assets Only
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-white leading-tight">Your Teaching Kit</h1>
        <p className="mt-2 text-sm text-white/70 leading-relaxed max-w-lg mx-auto">
          Perfect for <span className="font-semibold text-white">educators &amp; trainers</span> — the entire lesson in one download:
          every step image, a printable PDF guide, a ready-to-present Keynote/PowerPoint deck, a student worksheet, an answer key, and a quiz.
          No video — just <span className="font-semibold text-white">presentation and study-ready materials</span>.
        </p>
      </div>

      {/* Contents grid */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="kit-contents">
        {KIT_ITEMS.map(({ icon: Icon, label, desc }) => (
          <div key={label} className="sms-tweed flex items-start gap-3 rounded-xl border-2 border-[#2f4a2a] bg-[#0f0f16] p-3.5">
            <div className="w-9 h-9 rounded-lg bg-[#4f7746] flex items-center justify-center shrink-0"><Icon size={18} className="text-white" /></div>
            <div className="min-w-0">
              <div className="font-bold text-white text-sm flex items-center gap-1.5">
                {label} {status === "ready" && <CheckCircle2 size={14} className="text-[#8fbf82]" />}
              </div>
              <div className="text-xs text-white/55 leading-snug">{desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Build state / download */}
      <div className="mt-6">
        {status === "building" && (
          <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-5 text-center" data-testid="kit-building">
            <div className="flex items-center justify-center gap-2 text-[#8fbf82] font-extrabold"><Loader2 size={18} className="spin" /> Building your kit…</div>
            <p className="text-xs text-white/50 mt-1">Generating your lesson images and assembling every asset. This takes a minute.</p>
            <div className="mt-3 h-2.5 w-full rounded-full bg-black overflow-hidden border border-[#2f4a2a]">
              <div className="h-full bg-[#4f7746] transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
        {status === "ready" && (
          <button onClick={downloadAll} disabled={downloading} data-testid="kit-download-all-btn"
            className="w-full text-white font-extrabold text-lg py-4 rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            style={{ background: "#dc2626" }}>
            {downloading ? <Loader2 size={20} className="spin" /> : <Download size={20} />} Download All — .zip
          </button>
        )}
        {status === "error" && (
          <div className="rounded-2xl border-2 border-[#7a2b2b] bg-[#1a0f0f] p-5 text-center" data-testid="kit-error">
            <p className="text-sm text-white/80 font-semibold">Something interrupted the build.</p>
            <button onClick={() => { started.current = false; setStatus("building"); setProgress(5); window.location.reload(); }}
              className="mt-3 text-sm font-bold text-[#8fbf82] underline">Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}
